'use client';

import {
  Stack,
  Box,
  Typography,
  MenuItem,
  IconButton,
  Select,
} from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import { black, blue, slate } from '@/lib/colours';
import {
  formatPaginationDisplayedRows,
  formatPaginationNumber,
} from '@/lib/formatPaginationDisplayedRows';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

export type PaginationProps = {
  page: number;
  pageCount: number;
  pageSize: number;
  pageSizeOptions?: readonly number[];
  rowCount?: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
};

export function Pagination({
  page,
  pageCount,
  pageSize,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
  rowCount = 0,
  onPageChange,
  onPageSizeChange,
}: PaginationProps) {
  const availablePageSizeOptions = pageSizeOptions.length
    ? pageSizeOptions
    : DEFAULT_PAGE_SIZE_OPTIONS;
  const pageSizeSelectValue = availablePageSizeOptions.includes(pageSize)
    ? pageSize
    : '';
  const from = rowCount === 0 ? 0 : pageSize * page + 1;
  const to = Math.min(pageSize * (page + 1), rowCount);
  const showPageNavigation = rowCount > 0 || page > 0;

  const navButtonSx = (theme: Theme) => ({
    borderRadius: '4px',
    background: blue[50],
    ...theme.applyStyles('dark', {
      background: alpha(blue[400], 0.18),
    }),
    padding: '4px',
    svg: {
      fontSize: '16px',
    },
  });

  return (
    <Stack
      direction="row"
      gap={2}
      sx={{
        alignItems: 'center',
        bgcolor: 'background.default',
        borderRadius: 1,
        boxShadow: `0 2px 8px ${alpha(black[500], 0.08)}`,
        height: '36px',
        px: { xs: 0.5, sm: 1 },
        flex: 1,
        justifyContent: 'flex-end',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography
          color={slate[500]}
          sx={{
            fontSize: '12px',
            paddingInlineEnd: 1.5,
          }}
        >
          Rows per page:
        </Typography>
        <Select
          inputProps={{
            'data-dd-action-name': 'Pagination: Page Size',
          }}
          size="small"
          displayEmpty
          renderValue={() => formatPaginationNumber(pageSize)}
          sx={(theme) => ({
            background: 'background.paper',
            color: blue[600],
            minHeight: '28px',
            fontSize: '12px',
            [`& .MuiInputBase-input`]: {
              paddingBlock: 0.5,
              paddingInline: 1,
              fontSize: '12px',
              border: 0,
            },
            fieldset: {
              borderColor: blue[600],
            },
            svg: {
              color: blue[600],
              fontSize: '18px',
            },
            ...theme.applyStyles('dark', {
              color: blue[400],
              fieldset: { borderColor: blue[400] },
              svg: { color: blue[400] },
            }),
          })}
          value={pageSizeSelectValue}
          onChange={(event) => onPageSizeChange(Number(event.target.value))}
        >
          {availablePageSizeOptions.map((option) => (
            <MenuItem key={option} value={option} sx={{ fontSize: '12px' }}>
              {formatPaginationNumber(option)}
            </MenuItem>
          ))}
        </Select>
      </Box>
      {showPageNavigation && (
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography
            color={slate[500]}
            sx={{
              fontSize: '12px',
              paddingInlineEnd: 1.5,
            }}
          >
            {formatPaginationDisplayedRows({ from, to, count: rowCount })}
          </Typography>
          <Stack direction="row" gap="2px">
            <IconButton
              data-dd-action-name="Pagination: First Page"
              title="Go to first page"
              size="small"
              color="info"
              disabled={page <= 0}
              sx={navButtonSx}
              onClick={() => onPageChange(0)}
            >
              <FirstPageIcon />
            </IconButton>
            <IconButton
              data-dd-action-name="Pagination: Previous Page"
              title="Go to previous page"
              size="small"
              color="info"
              disabled={page <= 0}
              sx={navButtonSx}
              onClick={() => onPageChange(page - 1)}
            >
              <ArrowBackIcon />
            </IconButton>
            <IconButton
              data-dd-action-name="Pagination: Next Page"
              title="Go to next page"
              size="small"
              color="info"
              disabled={page >= pageCount - 1}
              sx={navButtonSx}
              onClick={() => onPageChange(page + 1)}
            >
              <ArrowForwardIcon />
            </IconButton>
            <IconButton
              data-dd-action-name="Pagination: Last Page"
              title="Go to last page"
              size="small"
              color="info"
              disabled={page >= pageCount - 1}
              sx={navButtonSx}
              onClick={() => onPageChange(Math.max(pageCount - 1, 0))}
            >
              <LastPageIcon />
            </IconButton>
          </Stack>
        </Box>
      )}
    </Stack>
  );
}
